import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import { findFeatureAtLatLng, findLayerOnMap } from 'utilities/mapPopupUtils';

import CustomReactPopup from './CustomReactPopup';

// Popup which presents data from several map layers for a single click, one page per layer.
// Driven by the optional "popupPages" array on a mapSet in the topic configuration:
//
//   popupPages: [
//       { pageLabel, layerId, popupContents },
//       ...
//   ]
//
// The first page is the "anchor" -- the layer that was clicked and has this popup bound to
// it. Every other page names another layer on the map, whose feature is resolved from the
// clicked point. A page may omit popupContents, in which case the mapSet's own
// popupContents is used (that is how the anchor page stays identical to the standard,
// non-paginated popup for that layer set).
const PaginatedPopup = ({
    pages,
    clickedLayerId, // id of the layer whose feature was clicked
    clickedFeature, // properties from the clicked layer's feature
    clickedLatLng, // L.LatLng from the popupopen event
    map, // leaflet map instance, used to find the other pages' layers
    popup, // L.Popup instance, so we can resize it as pages change
    selectedLayerSet,
    layer,
}) => {
    const [currentPage, setCurrentPage] = useState(0);

    // Resolved feature data per page, keyed by layer id. Pre-populated with the clicked
    // layer's data so the first page renders immediately.
    const [pageFeatures, setPageFeatures] = useState(() => ({
        [clickedLayerId]: clickedFeature,
    }));

    // Resolve the other pages' features from the layers currently on the map:
    useEffect(() => {
        if (!clickedLatLng || !clickedFeature) {
            return;
        }

        const resolved = {};

        pages.forEach((page) => {
            // The clicked page - already have this data from the click event:
            if (page.layerId === clickedLayerId) {
                resolved[page.layerId] = clickedFeature;
                return;
            }

            // All other pages - find the feature underneath the click in that layer.
            // Looked up here rather than when the popup was bound, so that layers which
            // finished loading later are still found:
            const siblingLayer = findLayerOnMap(map, page.layerId);
            if (!siblingLayer) {
                return;
            }

            resolved[page.layerId] = findFeatureAtLatLng(
                siblingLayer,
                clickedLatLng
            );
        });

        setPageFeatures(resolved);
    }, [clickedLatLng, clickedFeature, clickedLayerId, pages, map]);

    // Leaflet measures a popup when it opens, but React fills the container afterwards and
    // each page is a different height. Ask leaflet to re-measure so the popup frame is
    // sized correctly and stays anchored to the feature:
    useEffect(() => {
        popup?.update();
    }, [currentPage, pageFeatures, popup]);

    const activePage = pages[currentPage];
    const activeFeature = pageFeatures[activePage.layerId];

    return (
        <div className="paginated-popup">
            {activeFeature ? (
                <CustomReactPopup
                    feature={activeFeature}
                    selectedLayerSet={{
                        ...selectedLayerSet,
                        // Pages may inherit the layer set's popupContents:
                        popupContents:
                            activePage.popupContents ??
                            selectedLayerSet.popupContents,
                    }}
                    layer={layer}
                />
            ) : (
                <div className="popup-unavailable">
                    No data available for this area.
                </div>
            )}

            {/* Pagination controls */}
            {pages.length > 1 ? (
                <div className="popup-pagination">
                    {pages.map((page, pageIndex) => (
                        <Button
                            key={page.layerId}
                            className={`popup-pagination-tab${
                                pageIndex === currentPage ? ' active' : ''
                            }`}
                            size="small"
                            aria-current={pageIndex === currentPage}
                            onClick={() => setCurrentPage(pageIndex)}
                        >
                            {page.pageLabel}
                        </Button>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

export default PaginatedPopup;
